// // /////////////////////////////////////////////////////////////////////////////////////
// //                              Copyright (c) 2021 - 2025
// //                                    RXO Inc.
// //                          All Rights Reserved Worldwide
// //
// // WARNING:  This program (or document) is unpublished, proprietary
// // property of RXO Inc. and is to be maintained in strict confidence.
// // Unauthorized reproduction, distribution or disclosure of this program
// // (or document), or any program (or document) derived from it is
// // prohibited by State and Federal law, and by local law outside of the U.S.
// // /////////////////////////////////////////////////////////////////////////////////////

import { createSlice } from "@reduxjs/toolkit";
import { every, forEach, groupBy, sumBy, uniq } from "lodash";
import { add, addMonths } from "date-fns";

import { getInboundOutboundRatio, getLoadHistory } from "../api/loadHistory";
import getAnalyticsGridView from "../helpers/getAnalyticsGridViewName";
import groupLoadsByLane from "../helpers/groupLoadsByLane";
import pricingSliceHelper from "../helpers/pricingSliceHelper";
import { round } from "../helpers/laneCalculations";
import {
  analyticsView,
  order,
  analyticsFilterRankTypes,
  division as divisionType,
  currencyType,
  analyticsDateRangeOptions,
  analyticsForecastedRangeOptions,
  filterEquipmentTypes
} from "../helpers/types";
import groupLoadsByShipper from "../helpers/groupLoadsByShipper";
import messages from "../helpers/messages";
import getDefaultRadius from "../helpers/getDefaultRadius";
import { axiosAll, axiosSpread } from "../api/axios";
import {
  getAggregatedPricingForecastForLane,
  getShipperRatesForLane
} from "../api/lanes";
import { getDatRates } from "../api/dat";
import getShipperRates from "../helpers/getShipperRates";
import getEquipmentsForAnalyticsFilters from "../helpers/getEquipmentsForAnalyticsFilters";

const commonProps = {
  lanes: null,
  columnFilterMenu: { field: null, visible: false },
  columnFilters: {},
  filtersApplied: false,
  rangeFilters: {},
  columnWidths: {},
  offset: 0,
  sortField: null,
  sortOrder: order.ascending
};

const getOneYearOldDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  date.setHours(0, 0, 0, 0);
  return date;
};

const refreshDetailsGrid = state => {
  if (state.carrierView?.lanes) {
    setCarrierViewData(state, {
      payload: {
        analyticsGridView: state.selectedAnalyticsGridView,
        defaultIndex: state.selectedRowIndex
      }
    });
  } else if (state.loadView?.lanes) {
    setLoadViewData(state, {
      payload: {
        analyticsGridView: state.selectedAnalyticsGridView,
        defaultIndex: state.selectedRowIndex
      }
    });
  }
};

export const initialState = {
  isFullScreenMode: false,
  filters: {
    startDate: getOneYearOldDate().toISOString(),
    endDate: null,
    monthRange: analyticsDateRangeOptions.find(i => i.default),
    forecastStartDate: new Date().toISOString(),
    forecastEndDate: add(new Date(), {
      months: analyticsForecastedRangeOptions.find(i => i.default).months
    }).toISOString(),
    forecastedRange: analyticsForecastedRangeOptions.find(i => i.default),
    equipments: null,
    ranks: analyticsFilterRankTypes,
    loadAttributes: null,
    origin: null,
    originRadius: 50,
    destination: null,
    destinationRadius: 50
  },
  selectedAnalyticsGridView: analyticsView.laneView,
  isFetching: false,
  isFailure: false,
  loads: null,
  selectedRowIndex: null,
  selectedButtonIndex: null,
  laneView: commonProps,
  loadView: commonProps,
  shipperView: commonProps,
  carrierView: commonProps,
  shipperRatesView: {
    ...commonProps,
    laneOrigin: null,
    laneDestination: null,
    laneOriginRadius: null,
    laneDestinationRadius: null
  },
  inbound: null,
  outbound: null,
  verticalGraphType: null,
  breakdownGraphType: null,
  analyticsGraphType: null,
  aggregatedForecastPricing: null,
  datRate: {
    isFetching: false,
    isFailure: false,
    datRates: null
  },
  pollingLaneForecast: false
};

const setCarrierViewData = (state, { payload }) => {
  state.loadView = initialState.loadView;
  const gridViewName = getAnalyticsGridView(payload.analyticsGridView);
  const { carrierIds, loadIds } = state[gridViewName].lanes.find(
    x => x.metaData.defaultIndex === payload.defaultIndex
  );
  const filteredLoads = state.loads.filter(
    x =>
      carrierIds.some(y => y === x.carrierId) &&
      loadIds.some(id => id === x.loadId)
  );
  const groupedByCarrier = groupBy(filteredLoads, x => x.carrierId);
  const data = [];
  forEach(groupedByCarrier, groupedData => {
    data.push({
      carrierName: groupedData[0].carrierName,
      volume: groupedData.length,
      cost: round(sumBy(groupedData, "cost") / groupedData.length),
      margin: round(sumBy(groupedData, "margin") / groupedData.length),
      rate: round(sumBy(groupedData, "rate") / groupedData.length),
      metaData: {
        matched: true,
        defaultIndex: data.length,
        disabled: groupedData.every(l => l.exclude)
      },
      exclude: groupedData.every(l => l.exclude)
        ? messages.include.defaultMessage
        : messages.exclude.defaultMessage,
      loadIds: groupedData.map(l => l.loadId)
    });
  });
  data.map((x, index) => (x.metaData.defaultIndex = index));
  state.carrierView.lanes = data;
  state.selectedRowIndex = payload.defaultIndex;
  state.selectedButtonIndex = payload.defaultIndex;
};

const setLoadViewData = (state, { payload }) => {
  state.carrierView = initialState.carrierView;
  state.selectedButtonIndex = null;
  const gridViewName = getAnalyticsGridView(payload.analyticsGridView);
  const loadIds = state[gridViewName].lanes.find(
    x => x.metaData.defaultIndex === payload.defaultIndex
  ).loadIds;
  const filteredLoads = state.loads.filter(
    x => loadIds.some(y => y === x.loadId) && x
  );
  state.loadView.lanes = filteredLoads.map((load, index) => ({
    ...load,
    cpm: load.miles ? load.cost / load.miles : load.cost,
    exclude: load.exclude
      ? messages.include.defaultMessage
      : messages.exclude.defaultMessage,
    metaData: {
      matched: true,
      defaultIndex: index,
      disabled: load.exclude
    },
    loadIds: [load.loadId]
  }));
  state.selectedRowIndex = payload.defaultIndex;
};

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    setFullScreenMode: (state, { payload }) => {
      state.isFullScreenMode = payload;
    },
    setFilters: (state, { payload: filters }) => {
      state.filters = filters;
    },
    setAnalyticsGridView: (state, { payload }) => {
      state.selectedAnalyticsGridView = payload;
      state.selectedRowIndex = null;
      state.selectedButtonIndex = null;
      state.loadView = initialState.loadView;
      state.carrierView = initialState.carrierView;
    },
    requestLoadHistory: state => {
      state.loads = null;
      state.isFetching = true;
      state.isFailure = false;
    },
    receiveLoadHistorySucess: (state, { payload }) => {
      state.isFetching = false;
      state.isFailure = false;
      state.loads = payload;
      state.laneView = {
        ...commonProps,
        lanes: groupLoadsByLane(payload)
      };
      state.shipperView = {
        ...commonProps,
        lanes: groupLoadsByShipper(payload)
      };
      state.loadView = commonProps;
      state.carrierView = commonProps;
      state.selectedRowIndex = null;
      state.selectedButtonIndex = null;
    },
    receiveLoadHistoryFailure: state => {
      state.isFetching = false;
      state.isFailure = true;
      state.isFullScreenMode = false;
    },
    setColumnWidth: (state, payload) => {
      const gridViewName = getAnalyticsGridView(
        payload.payload.analyticsGridView
      );
      pricingSliceHelper.setColumnWidth(state[gridViewName], payload);
    },
    toggleColumnFilterMenu: (state, payload) => {
      const gridViewName = getAnalyticsGridView(
        payload.payload.analyticsGridView
      );
      pricingSliceHelper.toggleColumnFilterMenu(state[gridViewName], payload);
    },
    filterLoads: (state, payload) => {
      const gridViewName = getAnalyticsGridView(
        payload.payload.analyticsGridView
      );
      pricingSliceHelper.filterPricingLanes(state[gridViewName], payload);
    },
    sortLoads: (state, action) => {
      const gridViewName = getAnalyticsGridView(
        action.payload.analyticsGridView
      );
      pricingSliceHelper.sortPricingLanes(state[gridViewName], action);
    },
    resetLoadFilters: state => {
      Object.keys(analyticsView).map(x =>
        pricingSliceHelper.resetPricingGridFilters(state[x], initialState[x])
      );
      const enableAllRows = lanes =>
        lanes?.forEach(l => {
          l.metaData.disabled = false;
          l.volume = l.loadIds.length;
          l.exclude = messages.exclude.defaultMessage;
        });
      enableAllRows(state.laneView.lanes);
      enableAllRows(state.shipperView.lanes);

      state.loads?.forEach(l => (l.exclude = false));
      state.laneView.lanes = groupLoadsByLane(state.loads);
      state.shipperView.lanes = groupLoadsByShipper(state.loads);

      refreshDetailsGrid(state);
    },
    setLoadView: setLoadViewData,
    setCarrierView: setCarrierViewData,
    toggleExclude: (state, { payload }) => {
      const loads = state.loads;
      const gridViewName = getAnalyticsGridView(payload.analyticsGridView);

      if (payload.defaultIndex === undefined) {
        const filteredLoads = state[gridViewName].lanes?.filter(
          x => x.metaData.matched === true
        );
        const areAllLoadsExcluded = every(filteredLoads, {
          exclude: "Include"
        });
        let loadIds = [];
        filteredLoads.map(x => x.loadIds.map(y => loadIds.push(y)));

        loads.forEach(load => {
          if (loadIds.some(id => id === load.loadId)) {
            load.exclude = !areAllLoadsExcluded;
          }
        });
      } else {
        const {
          loadIds,
          metaData: { disabled }
        } = state[gridViewName].lanes.find(
          x => x.metaData.defaultIndex === payload.defaultIndex
        );

        loads.forEach(load => {
          if (loadIds.some(id => id === load.loadId)) {
            load.exclude = !disabled;
          }
        });
      }

      const updateExcludeFlag = gridViewType => {
        const grid = state[getAnalyticsGridView(gridViewType)];
        grid.lanes?.forEach(l => {
          const includedLoads = loads.filter(
            ld => !ld.exclude && l.loadIds.some(loadId => loadId === ld.loadId)
          );

          l.volume = includedLoads.length;
          l.metaData.disabled = includedLoads.length === 0;
          l.exclude =
            includedLoads.length === 0
              ? messages.include.defaultMessage
              : messages.exclude.defaultMessage;

          l.carriers = uniq(includedLoads.map(ld => ld.carrierId)).length;
          const sumOfCost = sumBy(includedLoads, "cost");
          const sumOfMiles = sumBy(includedLoads, l => l.miles || 1);

          l.cost = includedLoads.length
            ? round(sumOfCost / includedLoads.length)
            : 0;
          l.cpm = includedLoads.length ? round(sumOfCost / sumOfMiles) : 0;
          l.distance = includedLoads.length
            ? round(sumBy(includedLoads, il => il.miles) / includedLoads.length)
            : 0;
          l.margin = includedLoads.length
            ? round(sumBy(includedLoads, "margin") / includedLoads.length)
            : 0;
          l.rate = includedLoads.length
            ? round(sumBy(includedLoads, "rate") / includedLoads.length)
            : 0;
        });
      };
      updateExcludeFlag(analyticsView.laneView);
      updateExcludeFlag(analyticsView.shipperView);
      refreshDetailsGrid(state);
    },
    setFiltersFromLane: (state, { payload: { lane, division } }) => {
      const origin =
        division === divisionType.eu
          ? `${lane.originPlaceName}, ${lane.originCountryCode}`
          : `${lane.originCity}, ${lane.originState}`;
      const destination =
        division === divisionType.eu
          ? `${lane.destinationPlaceName}, ${lane.destinationCountryCode}`
          : `${lane.destinationCity}, ${lane.destinationState}`;
      const equipments = getEquipmentsForAnalyticsFilters(
        division,
        state.filters.equipments
      );

      state.filters = {
        ...state.filters,
        ...getDefaultRadius(lane, division),
        equipments,
        origin: {
          value: origin,
          label: origin,
          latitude: lane.originLatitude,
          longitude: lane.originLongitude,
          cityId: lane.originCityId ?? 0,
          cityName: lane.originCity,
          stateCode: lane.originState,
          countryCode2: lane.originCountry
        },
        destination: {
          value: destination,
          label: destination,
          latitude: lane.destinationLatitude,
          longitude: lane.destinationLongitude,
          cityId: lane.destinationCityId ?? 0,
          cityName: lane.destinationCity,
          stateCode: lane.destinationState,
          countryCode2: lane.destinationCountry
        }
      };
    },
    setInboundOutbound: (state, { payload }) => {
      state.inbound = payload.inbound;
      state.outbound = payload.outbound;
    },
    setVerticalGraphType: (state, { payload }) => {
      state.verticalGraphType = payload;
    },
    setBreakdownGraphType: (state, { payload }) => {
      state.breakdownGraphType = payload;
    },
    setAnalyticsGraphType: (state, { payload }) => {
      state.analyticsGraphType = payload;
    },
    setAggregatedForecastPricing: (state, { payload }) => {
      state.aggregatedForecastPricing = payload;
    },
    requestDatRates: state => {
      state.datRate.isFetching = true;
      state.datRate.isFailure = false;
      state.datRate.datRates = null;
    },
    receiveDatRatesSuccess: (state, { payload }) => {
      state.datRate.isFetching = false;
      state.datRate.isFailure = false;
      state.datRate.datRates = payload;
    },
    receiveDatRatesFailure: state => {
      state.datRate.isFetching = false;
      state.datRate.isFailure = true;
      state.datRate.datRates = null;
    },
    setShipperRates: (state, { payload }) => {
      state.shipperRatesView.lanes =
        payload === null ? null : getShipperRates(payload);
      state.shipperRatesView.laneOrigin = `${state.filters.origin.cityName}, ${state.filters.origin.stateCode}`;
      state.shipperRatesView.laneDestination = `${state.filters.destination.cityName}, ${state.filters.destination.stateCode}`;
      state.shipperRatesView.laneOriginRadius = `${state.filters.originRadius}`;
      state.shipperRatesView.laneDestinationRadius = `${state.filters.destinationRadius}`;
    },
    setPollingLaneForecast: (state, { payload }) => {
      state.pollingLaneForecast = payload;
    },
    reset: () => initialState
  }
});

export const {
  setFullScreenMode,
  setFilters,
  setAnalyticsGridView,
  setColumnWidth,
  sortLoads,
  toggleColumnFilterMenu,
  filterLoads,
  requestLoadHistory,
  receiveLoadHistorySucess,
  receiveLoadHistoryFailure,
  resetLoadFilters,
  setLoadView,
  setCarrierView,
  toggleExclude,
  setFiltersFromLane,
  setInboundOutbound,
  setVerticalGraphType,
  setBreakdownGraphType,
  setAnalyticsGraphType,
  setAggregatedForecastPricing,
  requestDatRates,
  receiveDatRatesSuccess,
  receiveDatRatesFailure,
  setShipperRates,
  setPollingLaneForecast,
  reset
} = analyticsSlice.actions;

export const analyticsReducer = analyticsSlice.reducer;

export const fetchLoadHistory =
  ({ division, lane, analyticsFilters }) =>
  async (dispatch, getState) => {
    const { enableShipperRatesView } = getState().featureFlags;

    if (analyticsFilters) {
      dispatch(setFilters(analyticsFilters));
    } else {
      dispatch(
        setFiltersFromLane({
          lane,
          division
        })
      );
    }
    const { filters } = getState().analytics;
    const payload = {
      originLatitude: filters.origin.latitude,
      originLongitude: filters.origin.longitude,
      originRadiusMiles: filters.originRadius,
      destinationLatitude: filters.destination.latitude,
      destinationLongitude: filters.destination.longitude,
      destinationRadiusMiles: filters.destinationRadius,
      startDate: filters.startDate,
      endDate: filters.endDate,
      equipmentTypes: filters.equipments?.map(e => e.value),
      ranks: filters.ranks?.map(e => e.value),
      loadRequirements: filters.loadAttributes?.map(e => e.value),
      currency:
        division === divisionType.coy ? currencyType.USD : currencyType.EUR
    };

    const inboundOutboundPayload = {
      latitude: filters.origin.latitude,
      longitude: filters.origin.longitude,
      radiusMiles: filters.originRadius,
      startDate: filters.startDate,
      endDate: filters.endDate
    };

    const loadSearchRequest = getLoadHistory(payload);
    const inboundOutboundRequest = getInboundOutboundRatio(
      inboundOutboundPayload
    );

    let requestArr = [];
    requestArr.push(loadSearchRequest);
    requestArr.push(inboundOutboundRequest);

    if (enableShipperRatesView && division === divisionType.coy) {
      const shipperRatesPayload = {
        originCityId: filters.origin.cityId,
        destinationCityId: filters.destination.cityId,
        effectiveDateUtc: filters.startDate,
        expirationDateUtc:
          filters.endDate ??
          addMonths(new Date(filters.startDate), filters.monthRange.months),
        equipmentTypes: filters.equipments?.map(e => e.value)
      };

      const shipperRatesRequest = getShipperRatesForLane(shipperRatesPayload);

      requestArr.push(shipperRatesRequest);
    }

    dispatch(requestLoadHistory());
    dispatch(fetchAggregatedPricingForecastForLane(division));

    axiosAll(requestArr)
      .then(
        axiosSpread((...responses) => {
          dispatch(receiveLoadHistorySucess(responses[0].data));
          dispatch(setInboundOutbound(responses[1].data));

          if (enableShipperRatesView && division === divisionType.coy) {
            dispatch(setShipperRates(responses[requestArr.length - 1].data));
          } else {
            dispatch(setShipperRates(null));
          }
        })
      )
      .catch(() => {
        dispatch(receiveLoadHistoryFailure());
      });
  };

export const fetchDatRates = () => async (dispatch, getState) => {
  const { filters } = getState().analytics;
  let eq = filterEquipmentTypes.find(x => x.label === "V").value;

  if (filters.equipments?.length === 1) {
    if (
      filters.equipments[0].label === "V" ||
      filters.equipments[0].label === "R" ||
      filters.equipments[0].label === "F"
    ) {
      eq = filters.equipments[0].value;
    }
  }

  const rateViewRequest = {
    originCityName: filters.origin.cityName,
    originStateCode: filters.origin.stateCode,
    originPostalCode: filters.origin.postalCode,
    destinationCityName: filters.destination.cityName,
    destinationStateCode: filters.destination.stateCode,
    destinationPostalCode: filters.destination.postalCode,
    equipment: eq
  };

  dispatch(requestDatRates());

  try {
    const datRates = (await getDatRates(rateViewRequest)).data;

    dispatch(receiveDatRatesSuccess(datRates));
  } catch {
    dispatch(receiveDatRatesFailure());
  }
};

export const fetchAggregatedPricingForecastForLane =
  division => async (dispatch, getState) => {
    const {
      analytics: { filters }
    } = getState();

    if (division === divisionType.coy) {
      let eq = filterEquipmentTypes.find(x => x.label === "V").value;

      if (filters.equipments?.length === 1) {
        if (
          filters.equipments[0].label === "V" ||
          filters.equipments[0].label === "R"
        ) {
          eq = filters.equipments[0].value;
        }
      }

      const aggregatedPricingForecastPayload = {
        originCityId: filters.origin.cityId,
        originCityName: filters.origin.cityName,
        originStateCode: filters.origin.stateCode,
        originCountry: filters.origin.countryCode2,
        originLatitude: filters.origin.latitude,
        originLongitude: filters.origin.longitude,
        destinationCityId: filters.destination.cityId,
        destinationCityName: filters.destination.cityName,
        destinationStateCode: filters.destination.stateCode,
        destinationCountry: filters.destination.countryCode2,
        destinationLatitude: filters.destination.latitude,
        destinationLongitude: filters.destination.longitude,
        equipmentTypes: [eq],
        forecastStartDate: filters.forecastStartDate,
        forecastEndDate: filters.forecastEndDate,
        aggregationPeriod: 1 //Month
      };

      let attempts = 1;
      const maxAttempts = 7;
      const interval = 3000;

      dispatch(setPollingLaneForecast(true));

      try {
        await pollLaneForecast(
          aggregatedPricingForecastPayload,
          attempts,
          maxAttempts,
          interval,
          forecast => {
            dispatch(setAggregatedForecastPricing(forecast));
            dispatch(setPollingLaneForecast(false));
          }
        );
      } catch {
        dispatch(setAggregatedForecastPricing(null));
        dispatch(setPollingLaneForecast(false));
      }
    }
  };

const pollLaneForecast = async (
  aggregatedPricingForecastPayload,
  attempts,
  maxAttempts,
  interval,
  callback
) => {
  const forecast = (
    await getAggregatedPricingForecastForLane(aggregatedPricingForecastPayload)
  ).data;

  if (forecast?.laneForecasts.length === 0 && attempts < maxAttempts) {
    attempts += 1;
    setTimeout(
      () =>
        pollLaneForecast(
          aggregatedPricingForecastPayload,
          attempts,
          maxAttempts,
          interval,
          callback
        ),
      interval
    );
  } else {
    callback && callback(forecast);
  }
};
